/**
 * WHAT A ROW BOUGHT IN A MONTH, ACCORDING TO THE BOOK.
 *
 * The Monthly Plan and `+ Lot` are two independent writers of the same fact and neither could
 * see the other. `saveAddLot` appends a lot, blends the average, debits cash and books a
 * `portfolio_buy`. `saveMo` appends nothing, blends the average, moves no cash and writes a
 * month entry. A purchase entered through `+ Lot` therefore still rendered as `Pending` on the
 * plan, and pressing `Confirm & Save` on it added the quantity a SECOND time.
 *
 * Measured on 2026-08: sixteen of the month's twenty-six planned rows were already recorded as
 * lots — TCELL 95 @ 106.81 among them — and the screen invited the owner to save all sixteen
 * again. He asked the right question: "why am I not marking what I bought as bought?" He was
 * not marking anything. There was no mark. There was a second entry form wearing a checklist's
 * clothes.
 *
 * This module is the one definition of the answer, so the screen, the guard and the integrity
 * check cannot disagree about it. It imports nothing and touches nothing: given a holding and a
 * month key it reports what the lot list says, or null.
 *
 * The rule it encodes is not new. `math.js:179` has said since 2026-08-01 that a recorded
 * purchase beats the plan whatever the month's status; `portfolio.js` and `calcHoldingXIRR`
 * both short-circuit on `h.lots`. Three readers already treat the lot as the record. Only the
 * plan screen did not. (audit 2026-09-03)
 */

/** Investment lots (never DRIP — a reinvested distribution is not a planned buy) in `monthKey`. */
export function monthLots(holding, monthKey) {
  if (!holding || !/^\d{4}-\d{2}$/.test(String(monthKey || ''))) return [];
  return (holding.lots || []).filter(l =>
    l && l.type !== 'drip' && String(l.date || '').startsWith(monthKey));
}

/**
 * The month entry those lots imply, or null.
 *
 * `price` is the COST-WEIGHTED net price, so `qty * price === cost` holds exactly. Every reader
 * of a month entry computes `qty * price` and would otherwise get a different number from the
 * one the lots recorded. Lot prices are already net of commission (`pricePurchase` folds it in),
 * so no fee is added here and none is lost.
 */
export function lotEntry(holding, monthKey) {
  const lots = monthLots(holding, monthKey);
  if (!lots.length) return null;
  let qty = 0, cost = 0;
  lots.forEach(l => {
    const q = Number(l.qty) || 0;
    qty += q;
    cost += (l.cost != null ? Number(l.cost) : q * (Number(l.price) || 0)) || 0;
  });
  if (!(qty > 0)) return null;
  return {
    qty,
    price: cost / qty,
    cost,
    source: 'lots',
    lotIds: lots.map(l => l.id).filter(Boolean),
  };
}

/** True for an entry this module wrote — i.e. one whose money has already moved elsewhere. */
export function isLotSourced(entry) {
  return !!(entry && entry.source === 'lots');
}

/**
 * The contradiction that must never exist: a row with lots in the month AND a month entry that
 * did not come from them. That is the same purchase counted twice — once in `h.qty` by
 * `saveAddLot` and once again by `saveMo` — and there is no benign reading of it.
 *
 * Returned as data so the integrity page can name the rows rather than assert a number.
 */
export function lotEchoes(holdings, monthKey, entries) {
  const out = [];
  const e = entries || {};
  (holdings || []).forEach(h => {
    if (!h || !h.ticker) return;
    const row = e[h.ticker];
    if (!row || row.skipped || isLotSourced(row)) return;
    const lots = monthLots(h, monthKey);
    if (!lots.length) return;
    out.push({ ticker: h.ticker, month: monthKey, entryQty: Number(row.qty) || 0,
      lotQty: lots.reduce((a, l) => a + (Number(l.qty) || 0), 0) });
  });
  return out;
}
